import React, { FC } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, SubmitHandler, useFieldArray, FormProvider, FieldPath } from 'react-hook-form';
import { ButtonContainer as Buttons } from '@jbc-year-end-adj/2024/features/questions/components/ButtonContainer';
import Button from 'jbc-front/components/Button';

import { Input } from '@jbc-year-end-adj/2024/components/form/Input';
import { DateField } from '@jbc-year-end-adj/2024/components/react-hook-form/DateField';
import { FileField } from '@jbc-year-end-adj/2024/components/react-hook-form/FileField';
import { SelectField } from '@jbc-year-end-adj/2024/components/react-hook-form/SelectField';
import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { FormAddButton } from '@jbc-year-end-adj/2024/components/form/FormAddButton';
import { DeletableSection } from '@jbc-year-end-adj/2024/components/ui/DeletableSection';
import { Grid } from '@jbc-year-end-adj/2024/components/ui/Grid';
import { Separator } from '@jbc-year-end-adj/2024/components/ui/Separator';
import { schema, Schema, generateDefaultValues, emptyFormerJob, FormerJobSchema } from './schema';
import { ANSWER_FORMER_JOBS, convertFormDataToQueryVariables } from './mutation';

import { Lead } from '@jbc-year-end-adj/2024/features/questions/templates/parts/Lead';
import { FilePreview } from '@jbc-year-end-adj/2024/features/FilePreview';
import { AmountField } from '@jbc-year-end-adj/2024/components/react-hook-form/AmountField';
import { withholdingSlipStatuses } from '../consts';
import { useEmployeeMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { ServerValidationError, eachValidationErrorsForHookFrom } from '@jbc-year-end-adj/2024/hooks/graphql/error/ServerValidationError';
import useReactRouter from 'use-react-router';
import { useEmployeeQuery } from '@jbc-year-end-adj/2024/hooks/graphql/useQuery';
import { IMAGE_SETTINGS, FormerJob, EMPLOYEE_OFFICE } from '../query';
import { LoadingPage } from '../../../Loading';
import { UnavailableAlert } from './UnavailableAlert';

type FormProps = {
  formerJobs: FormerJob[];
};

export const FormerJobForm: FC<FormProps> = ({ formerJobs }) => {
  const { data, loading } = useEmployeeQuery(IMAGE_SETTINGS);
  const { data: officeData } = useEmployeeQuery(EMPLOYEE_OFFICE);
  const officeName = officeData?.request?.employee?.office.name;
  const previousWithholdingSlipImageSetting = data?.clientSetting?.requestImageUploadSettings.previousWithholdingSlip;
  const imageRequired = previousWithholdingSlipImageSetting === 'required';
  const { history } = useReactRouter();

  const methods = useForm<Schema>({
    defaultValues: generateDefaultValues(formerJobs),
    resolver: yupResolver(schema(imageRequired))
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    setValue,
    setError,
    control
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'formerJobs'
  });

  const [answer] = useEmployeeMutation(ANSWER_FORMER_JOBS);
  const isUnavailable = watch('formerJobs').some((formerJob: FormerJobSchema) => formerJob.withholdingSlipStatus === 'unavailable');
  const onSubmit: SubmitHandler<Schema> = async data => {
    try {
      const variables = convertFormDataToQueryVariables(data);
      await answer({ variables });
      history.push('/employee_input');
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema(imageRequired), data, (path, error) => {
          setError(path as FieldPath<Schema>, error);
        });
        return;
      }
      throw err;
    }
  };

  if (loading) return <LoadingPage />;
  return (
    <FormProvider {...methods}>
      <form>
        <FormSection>
          <Separator />
          <Lead>以下の情報の記入が必要です。</Lead>

          <Grid colGap>
            {fields.map((field, index) => {
              const withholdingSlipImage = watch(`formerJobs.${index}.withholdingSlipImage`);
              const withholdingSlipStatus = watch(`formerJobs.${index}.withholdingSlipStatus`);

              return (
                <DeletableSection key={field.id}>
                  <DeletableSection.Header onDelete={() => remove(index)}>前職情報（{index + 1}）</DeletableSection.Header>
                  <DeletableSection.Body>
                    <FormSection>
                      <Input
                        label="会社名"
                        required
                        error={errors.formerJobs && errors.formerJobs[index]?.companyName?.message}
                        isError={errors.formerJobs && !!errors.formerJobs[index]?.companyName?.message}
                        {...register(`formerJobs.${index}.companyName`)}
                      />

                      <Input
                        label="住所"
                        note="前職の住所を全角文字で入力してください。"
                        error={errors.formerJobs && errors.formerJobs[index]?.address?.message}
                        isError={errors.formerJobs && !!errors.formerJobs[index]?.address?.message}
                        {...register(`formerJobs.${index}.address`)}
                      />

                      <DateField
                        label="退職日"
                        required
                        error={errors.formerJobs && errors.formerJobs[index]?.resignedOn?.message}
                        name={`formerJobs.${index}.resignedOn`}
                      />

                      <DateField label="最後の給与支給日" name={`formerJobs.${index}.lastSalaryOn`} />

                      <SelectField
                        label="源泉徴収票"
                        required
                        placeholder="選択して下さい"
                        options={withholdingSlipStatuses}
                        error={errors.formerJobs && errors.formerJobs[index]?.withholdingSlipStatus?.message}
                        name={`formerJobs.${index}.withholdingSlipStatus`}
                        note="前職からもらった令和6年の源泉徴収票の原本は会社に提出する必要があります。年度が正しいか改めてご確認ください。退職所得の源泉徴収票は不要です。"
                      />
                      {withholdingSlipStatus === 'not_submitted' && (
                        <>
                          {previousWithholdingSlipImageSetting !== 'none' && withholdingSlipStatus === 'not_submitted' && (
                            <FileField
                              label="源泉徴収票画像"
                              note="提出済みの場合アップロードは不要です。"
                              name={`formerJobs.${index}.withholdingSlipImage`}
                              required={imageRequired}
                              error={errors.formerJobs && errors.formerJobs[index]?.withholdingSlipImage?.message}
                              isError={errors.formerJobs && !!errors.formerJobs[index]?.withholdingSlipImage?.message}
                            >
                              {withholdingSlipImage && (
                                <FileField.FileContainer>
                                  <FileField.File
                                    file={withholdingSlipImage}
                                    onRemove={() => setValue(`formerJobs.${index}.withholdingSlipImage`, null)}
                                  />
                                  <FilePreview file={withholdingSlipImage} />
                                </FileField.FileContainer>
                              )}
                            </FileField>
                          )}
                          <AmountField
                            label="支払金額"
                            note="源泉徴収票の「支払金額」欄に記載されている金額です。提出済みの場合記載は不要です。"
                            error={errors.formerJobs && errors.formerJobs[index]?.paymentAmount?.message}
                            isError={errors.formerJobs && !!errors.formerJobs[index]?.paymentAmount?.message}
                            {...register(`formerJobs.${index}.paymentAmount`)}
                            required
                          />

                          <AmountField
                            label="社会保険料等の金額"
                            note="源泉徴収票の「社会保険料等の金額」欄に記載されている金額です。提出済みの場合記載は不要です。"
                            error={errors.formerJobs && errors.formerJobs[index]?.deductionAmount?.message}
                            isError={errors.formerJobs && !!errors.formerJobs[index]?.deductionAmount?.message}
                            {...register(`formerJobs.${index}.deductionAmount`)}
                            required
                          />

                          <AmountField
                            label="源泉徴収税額"
                            note="源泉徴収票の「源泉徴収税額」欄に記載されている金額です。提出済みの場合記載は不要です。"
                            error={errors.formerJobs && errors.formerJobs[index]?.taxAmount?.message}
                            isError={errors.formerJobs && !!errors.formerJobs[index]?.taxAmount?.message}
                            {...register(`formerJobs.${index}.taxAmount`)}
                            required
                          />
                        </>
                      )}
                    </FormSection>
                  </DeletableSection.Body>
                </DeletableSection>
              );
            })}

            <FormAddButton onClick={() => append(emptyFormerJob)}>前職情報を追加</FormAddButton>
          </Grid>
        </FormSection>
        {isUnavailable && <UnavailableAlert officeName={officeName} />}
        <Buttons>
          <Button primary onClick={handleSubmit(onSubmit)}>
            次へ
          </Button>
        </Buttons>
      </form>
    </FormProvider>
  );
};
